import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Form } from 'react-bootstrap';
import { utilizationSettingsService as service } from '../../_services/utilizationSettings.service';

function validHours(value) {
    const hours = Number(value);

    return value !== '' && Number.isFinite(hours) && hours > 0 && hours <= 24;
}

function UtilitiesManagement({ company }) {
    const [savedHours, setSavedHours] = useState(null);
    const [hours, setHours] = useState('');
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const mounted = useRef(false);
    const savedRef = useRef(null);

    useEffect(() => {
        mounted.current = true;

        return () => {
            mounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (!company) return undefined;

        let disposed = false;
        savedRef.current = null;
        setSavedHours(null);
        setHours('');

        function applySaved(settings) {
            if (disposed) return;

            const previous = savedRef.current;
            savedRef.current = settings.availableHours;
            setSavedHours(settings.availableHours);
            setHours(current => previous === null || Number(current) === previous
                ? String(settings.availableHours)
                : current);
        }

        const read = service.getSettings();
        if (read.data) applySaved(read.data);

        read.revalidate
            .then(applySaved)
            .catch(err => {
                if (!disposed) setError(err.message || 'Unable to load available hours.');
            });

        return () => {
            disposed = true;
        };
    }, [company]);

    async function save() {
        const previous = savedHours;
        const next = Number(hours);
        setSaving(true);
        setSuccess(false);
        setError('');
        savedRef.current = next;
        setSavedHours(next);

        try {
            const confirmed = await service.saveSettings({ availableHours: next });
            if (!mounted.current) return;

            savedRef.current = confirmed.availableHours;
            setSavedHours(confirmed.availableHours);
            setHours(String(confirmed.availableHours));
            setSuccess(true);
        } catch (err) {
            if (!mounted.current) return;

            savedRef.current = previous;
            setSavedHours(previous);
            setError(err.message || 'Failed to save available hours. Please try again.');
        } finally {
            if (mounted.current) setSaving(false);
        }
    }

    const loading = savedHours === null;
    const hoursValid = validHours(hours);
    const dirty = !loading && Number(hours) !== savedHours;

    return (
        <section className="mt-5" aria-labelledby="utilization-hours-title">
            <h4 id="utilization-hours-title">Scheduled available time</h4>
            {success && <Alert variant="success" dismissible onClose={() => setSuccess(false)}>Available hours saved.</Alert>}
            {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
            <Card className="mb-3">
                <Card.Body>
                    <h5 className="mb-1">Utilization by Time</h5>
                    <p className="text-muted small mb-3">
                        Utilization by Time = Operating time ÷ (number of machines × scheduled available time) × 100.
                        Operating time is planned production time minus unplanned stop time, the same as in the daily OEE.
                    </p>
                    <Form.Group controlId="utilization-available-hours" style={{ maxWidth: '320px' }}>
                        <Form.Label>Scheduled available time (hours per machine per day)</Form.Label>
                        <Form.Control
                            type="number"
                            min="0.1"
                            max="24"
                            step="0.1"
                            value={hours}
                            disabled={loading || saving}
                            isInvalid={!loading && !hoursValid}
                            onChange={event => {
                                setSuccess(false);
                                setHours(event.target.value);
                            }}
                        />
                        <Form.Control.Feedback type="invalid">Enter a number more than 0 and at most 24.</Form.Control.Feedback>
                        <Form.Text muted>Default is 22 hours (24 hours minus 2 hours lunch break).</Form.Text>
                    </Form.Group>
                </Card.Body>
            </Card>
            <div className="d-flex gap-2">
                <Button onClick={save} disabled={!dirty || !hoursValid || saving}>
                    {saving ? 'Saving…' : 'Save available hours'}
                </Button>
                <Button variant="secondary" disabled={!dirty || saving} onClick={() => {
                    setHours(String(savedHours));
                    setError('');
                }}>Cancel</Button>
            </div>
        </section>
    );
}

export { UtilitiesManagement };
